/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author urahish
 * 01/20/2022
 *
 */

import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import axios from 'axios';
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";
import {toInteger} from "../../../../../utils/formValueUtils";

/**
 * HOC for the Course Role form
 */
class CourseRolesForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            courseRoleId: null,
            partnerName: '',
            partnerRoleId: '',
            partnerRoleName: '',
            pearsonRoleId: '',
            pearsonRoleName:'',
            partnerNameWithPartnerRole:'',
            isDisplayLoader: true,
            errors: {},

            token: '',
            fields: [
                {
                    inputType: 'dropdown',
                    labelName: 'Partner Role *',
                    inputName: 'partnerRoleId',
                    optionValueName: 'partnerRoleId',
                    optionLabelName: 'partnerNameWithPartnerRole',
                    required: true,
                    path: '/course-roles',
                    valueType: 'partner-roles'
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Pearson Role *',
                    inputName: 'pearsonRoleId',
                    optionValueName: 'pearsonRoleId',
                    optionLabelName: 'pearsonRoleName',
                    required: true,
                    path: '/course-roles',
                    valueType: 'pearson-roles'
                }
            ]
        };

        // get the parameter from URL
        const courseRoleId = this.props.match.params.id;

        // set icons to the header
        if (courseRoleId === undefined) {
            EventEmitter.emit('setPageHeaderData', {
                page: 'course-roles',
                pageName: "Course Roles",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/course-roles",
                optionalIcons: ['backIcon','clearFormIcon']
            });
        } else {
            EventEmitter.emit('setPageHeaderData', {
                page: 'course-roles',
                pageName: "Course Roles",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/course-roles",
                optionalIcons: ['backIcon','clearFormIcon','deleteIcon']
            });
        }

        // set the event emitters
        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.on('deleteFormData', this.deleteCourseRole);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.on('clearForm', this.clearForm);
    }

    // set a token to unsubscribe the async call
    CancelToken = axios.CancelToken;
    source = this.CancelToken.source();

    componentDidMount = async () => {
        // get parameter from URL
        const courseRoleId = this.props.match.params.id;

        // get token
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);

        // fill the form if not a create Course Role
        if (courseRoleId !== undefined) {

            const url = '/course-roles?search=%7BcourseRoleId:' + courseRoleId + '%7D';

            await API.get(url, {headers: {Authorization: token}, cancelToken: this.source.token})
                .then(res => {
                    const data = res.data[0];
                    oldState.courseRoleId = (data.courseRoleId !== undefined) ? data.courseRoleId : null;
                    oldState.partnerName = (data.partnerName !== undefined) ? data.partnerName : null;
                    oldState.partnerRoleId = (data.partnerRoleId !== undefined) ? data.partnerRoleId : null;
                    oldState.partnerRoleName = (data.partnerRoleName !== undefined) ? data.partnerRoleName : null;
                    oldState.pearsonRoleId = (data.pearsonRoleId !== undefined) ? data.pearsonRoleId : null;
                    oldState.pearsonRoleName = (data.pearsonRoleName !== undefined) ? data.pearsonRoleName : null;
                    oldState.partnerNameWithPartnerRole = (data.partnerNameWithPartnerRole !== undefined) ? data.partnerNameWithPartnerRole : null;
                    oldState.token = token;
                    oldState.isDisplayLoader = false;

                    this.setState(oldState);
                }).catch(er => {
                    oldState.isDisplayLoader = false;
                    this.setState(oldState);
                    this.displayErrorsForAPICalls(er);
                });
        } else {
            oldState.token = token;
            oldState.isDisplayLoader = false;
            this.setState(oldState);
        }
    };

    componentWillUnmount = () => {
        // remove all event emitters
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.off('deleteFormData', this.deleteCourseRole);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.off('clearForm', this.clearForm);
        this.source.cancel("cancel course roles async calls");
    };

    /**
     * set the state
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionValueName]: event.value,
                [event.optionLabelName]: event.label
            }, () => {
                this.validate(event.optionValueName, event.value)
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            }, () => {
                this.validate(key, val)
            });
        }
    };

    /**
     * handle the form submission
     */
    handleSubmit = () => {
        // data to be sent
        let data = null;
        // check its a update or create
        if (this.state.courseRoleId !== null) {
            data = {
                courseRoleId: this.state.courseRoleId,
                partnerName: this.state.partnerName,
                partnerRoleId: this.state.partnerRoleId,
                partnerRoleName: this.state.partnerRoleName,
                pearsonRoleId: this.state.pearsonRoleId,
                pearsonRoleName: this.state.pearsonRoleName
            };
        } else {
            data = {
                partnerRoleId: toInteger(this.state.partnerRoleId),
                pearsonRoleId: toInteger(this.state.pearsonRoleId)
            };
        }

        // validate each form field
        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        // check weather there are error messages
        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            // check update or create
            if (this.state.courseRoleId !== null) {
                this.updateCourseRoles(data);
            } else {
                this.createCourseRoles(data);
            }
        }
    };

    /**
     * validate the user inputs
     * @param key - input name
     * @param value - input value
     */
    validate = (key, value) => {
        let error = {};

        switch (key) {
            case 'partnerRoleId':
                if (this.state.partnerRoleId === '')
                    error[key] = "Please select a Partner Role!";
                else
                    error[key] = "";
                break;
            case 'pearsonRoleId':
                if (this.state.pearsonRoleId === '')
                    error[key] = "Please select a Pearson Role!";
                else
                    error[key] = "";
                break;
            default:
                break;
        }

        const newState = Object.assign({}, this.state);
        newState.errors = Object.assign(newState.errors, error);

        this.setState(newState);
    };

    /**
     * create Course Role
     * @param data - the data to create a new Course Role
     */
    createCourseRoles = async (data) => {
        await API.post('/course-roles',
            {
                "partnerRoleId": data.partnerRoleId,
                "pearsonRoleId": data.pearsonRoleId
            }, {headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(res => {
            this.setAlert("Success", "Success", "Successfully Created the Course Role");
            this.props.history.push('/home/course-roles');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    /**
     * update Course Role
     * @param data - updated Course Role details
     */
    updateCourseRoles = async (data) => {
        await API.put('/course-roles',
            {
                "courseRoleId": data.courseRoleId,
                "partnerRoleId": data.partnerRoleId,
                "pearsonRoleId": data.pearsonRoleId
            },{headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(response => {
            this.setAlert("Success", "Success", "Successfully updated the Course Role");
            this.props.history.push('/home/course-roles');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    /**
     * display delete warning message
     */
    displayDeleteWarningMessage = () => {
        if (this.state.courseRoleId !== null) {
            EventEmitter.emit('warningModelData', {
                type: 'course-roles',
                name: this.state.partnerNameWithPartnerRole
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "You are trying to delete before loading the Course Role Details",
                alertTitle: "Error"
            });
        }

    };

    /**
     * delete a Course Role
     * @param data
     */
    deleteCourseRole = async (data) => {
        if (data && (this.props.match.path === "/home/course-roles/:id")) {
            await API.delete('/course-roles?courseRoleId=' + this.state.courseRoleId, {headers: {Authorization: this.state.token}, cancelToken: this.source.token})
                .then(res => {
                    this.setAlert("Success", "Success", "Successfully Deleted the Course Role");
                    this.props.history.push('/home/course-roles');
                }).catch(er => {
                    this.displayErrorsForAPICalls(er);
                });
        }
    };

    displayErrorsForAPICalls = (error) => {
        if (error.response !== undefined && error.response.status === 404) {
            this.setAlert("Error", "Error", "The selected item not found!");
        } else if (error.message.includes('422')) {
            this.setAlert("Error", "Sorry", "Course Role is already exist!");
        } else if (error.response === undefined || error.response.status === 401) {
            localStorage.removeItem('tmcUser')
            this.props.history.push('/');
        } else {
            this.setAlert("Error", "Error", "Something went wrong");
        }
    };

    /**
     * fire an alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    /**
     * clear the form
     */
    clearForm = () => {
        const newState = Object.assign({}, this.state);

        newState.partnerName = "";
        newState.partnerRoleId = "";
        newState.partnerRoleName = "";
        newState.pearsonRoleId = "";
        newState.pearsonRoleName = "";
        newState.partnerNameWithPartnerRole = "";
        newState.errors = {};

        this.setState(newState);
    };

    /**
     * navigate to previous page
     */
    navigateToPreviousPage = () => {
        this.props.history.push('/home/course-roles');
    };

    render() {
        return(
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader/>}
                <div className="main-btn-container">
                    <div className="buttons" tooltip="Save" onClick={this.handleSubmit}>
                        <i className="fa fa-save fa-lg icon-primary" />
                    </div>
                </div>
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
            </div>
        )
    }
}

export default CourseRolesForm;
